import {
  TapStreamEncoderDecoder,
  TapStreamReader,
  TapStreamWriter,
} from '@iotize/tap/client/impl';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TapDataError } from '../tap-data-error';
import {
  EditableValueDataStreamInterface,
  KeyTypeType,
} from '../utility/editable-data-stream';
import { AbstractVariable } from './abstract-variable-stream';

export class ConverterVariableView<
  DataType,
  KeyType extends KeyTypeType = KeyTypeType,
> implements EditableValueDataStreamInterface<DataType, KeyType>
{
  get converter() {
    return this.context.converter;
  }

  get valueSnapshot(): DataType | undefined {
    const lastRawValue = this.context.variable.rawValueSnaphot;
    return lastRawValue !== undefined
      ? this.decodeValue(lastRawValue)
      : undefined;
  }

  get values(): Observable<DataType> {
    return this.context.variable.rawValues.pipe(
      map((v) => this.decodeValue(v)),
    );
  }

  constructor(
    public readonly id: KeyType,
    public context: {
      /**
       * The underlying variable from which the view will be created
       */
      variable: AbstractVariable<any, any>;
      /**
       * If set to false, Tap will read the current value before editing and writing the new index
       * If set to true and if Tap already know the current value, it will not perform a read before editing the value
       */
      useLastKnownValueBeforeWrite?: boolean;
      /**
       * Defined how the extracted value should be decoded
       */
      converter: TapStreamEncoderDecoder<DataType>;
    },
  ) {}

  async read(): Promise<DataType> {
    const result = await this.context.variable.readRaw();
    return this.decodeValue(result);
  }

  async write(value: DataType): Promise<any> {
    const stream = await this.getCurrentValueStream();
    this.encodeValue(value, stream);
    stream.pos = stream.getStreamSize();
    return this.context.variable.writeRaw(stream.toBytes);
  }

  private async getCurrentValueStream(): Promise<TapStreamWriter> {
    let currentValue: Uint8Array | undefined;
    if (this.context.useLastKnownValueBeforeWrite) {
      currentValue = this.context.variable.rawValueSnaphot;
    } else {
      currentValue = await this.context.variable.readRaw();
    }
    if (!currentValue) {
      currentValue = new Uint8Array();
    }
    return new TapStreamWriter(currentValue);
  }

  private encodeValue(value: DataType, stream: TapStreamWriter) {
    try {
      this.converter.encode(value, stream);
    } catch (err) {
      throw TapDataError.unexpectedEncodedValueError(this, value, err);
    }
  }

  private decodeValue(data: Uint8Array) {
    try {
      const stream = TapStreamReader.create(data);
      return this.converter.decode(stream);
    } catch (err) {
      throw TapDataError.unexpectedVariableDataRead(this, data, err);
    }
  }
}
